// =============================================================================
// MfaGate - pre-launch gate 1, client half.
//
// The database already enforces MFA: a staff session without aal2 at a practice
// that requires it resolves to no practice and no staff role, so every policy
// denies. This component makes that state recoverable instead of blank: it asks
// mfa_requirement() (readable pre-MFA on purpose) and, when the session is not
// satisfied, shows either TOTP enrollment (no verified factor yet) or a TOTP
// challenge (factor exists). After a successful verify the session is refreshed
// so the new JWT carries aal2 and the app proceeds normally.
//
// Grace period: mfa_requirement().in_grace lets staff through with a banner
// prompting enrollment before the enforcement date.
// =============================================================================
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "./AuthProvider";
import { C } from "../lib/tokens";
import { Btn, Card, Input, ErrorBanner } from "../components/ui";

export default function MfaGate({ children }) {
  const { isAuthenticated, role } = useAuth();
  const [state, setState] = useState({ loading: true, req: null });
  const [enroll, setEnroll] = useState(null);     // { factorId, qr, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dismissedBanner, setDismissedBanner] = useState(false);

  const check = async () => {
    const { data, error: err } = await supabase.rpc("mfa_requirement");
    if (err) { setState({ loading: false, req: null, error: err.message }); return; }
    const r = Array.isArray(data) ? data[0] : data;
    setState({ loading: false, req: r });
  };
  useEffect(() => { if (isAuthenticated && role && role !== "Patient") check(); else setState({ loading: false, req: null }); }, [isAuthenticated, role]);

  const startEnroll = async () => {
    setBusy(true); setError(null);
    const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "PracticeOS authenticator" });
    if (err) setError(err.message); else setEnroll({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
    setBusy(false);
  };

  const verify = async (factorId) => {
    if (!/^\d{6}$/.test(code)) { setError("Enter the 6-digit code from your authenticator app."); return; }
    setBusy(true); setError(null);
    try {
      const ch = await supabase.auth.mfa.challenge({ factorId });
      if (ch.error) throw ch.error;
      const v = await supabase.auth.mfa.verify({ factorId, challengeId: ch.data.id, code });
      if (v.error) throw v.error;
      await supabase.auth.refreshSession();
      setEnroll(null); setCode("");
      await check();
    } catch (e) { setError(e.message || "Verification failed"); }
    setBusy(false);
  };

  const challengeExisting = async () => {
    setBusy(true); setError(null);
    const { data, error: err } = await supabase.auth.mfa.listFactors();
    if (err) { setError(err.message); setBusy(false); return; }
    const f = (data.totp || []).find((x) => x.status === "verified") || (data.all || [])[0];
    if (!f) { setError("No authenticator is set up for this account."); setBusy(false); return; }
    setBusy(false);
    await verify(f.id);
  };

  if (!isAuthenticated || role === "Patient" || state.loading) return children;
  const req = state.req;
  if (!req || req.satisfied) {
    // Satisfied, or in grace: proceed (with a banner during grace).
    if (req && req.required && req.in_grace && !req.has_verified_factor && !dismissedBanner) {
      return (
        <>
          <div style={{ background: C.amberBg, borderBottom: "1px solid " + C.amberBorder, padding: "8px 14px", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span>Two-factor authentication is required for this practice from {req.enforce_after ? new Date(req.enforce_after).toLocaleDateString() : "soon"}. Set it up now so you are not locked out.</span>
            <span style={{ display: "flex", gap: 8 }}>
              <Btn variant="primary" size="sm" onClick={() => setState((s) => ({ ...s, req: { ...s.req, satisfied: false } }))}>Set up now</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setDismissedBanner(true)}>Later</Btn>
            </span>
          </div>
          {children}
        </>
      );
    }
    return children;
  }

  // Not satisfied: enrollment or challenge, full screen. Nothing behind it renders.
  const wrap = (inner) => (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bgSecondary, padding: 24 }}>
      <Card style={{ maxWidth: 440, width: "100%" }}>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        {inner}
        <div style={{ marginTop: 14, fontSize: 12, color: C.textSecondary }}>
          Your practice requires two-factor authentication to view patient information. <a href="#" onClick={(e) => { e.preventDefault(); supabase.auth.signOut({ scope: "local" }); }}>Sign out</a>
        </div>
      </Card>
    </div>
  );

  if (!req.has_verified_factor) {
    return wrap(
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Set up two-factor authentication</div>
        {!enroll ? (
          <>
            <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 14 }}>You will scan a QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Authy) and enter the 6-digit code it shows.</div>
            <Btn variant="primary" disabled={busy} onClick={startEnroll}>{busy ? "Preparing..." : "Start setup"}</Btn>
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "center", margin: "10px 0" }}><img src={enroll.qr} alt="Authenticator QR code" style={{ width: 200, height: 200 }} /></div>
            <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 10, wordBreak: "break-all" }}>Cannot scan? Enter this key manually: <code>{enroll.secret}</code></div>
            <Input label="6-digit code" value={code} onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))} />
            <Btn variant="primary" disabled={busy} onClick={() => verify(enroll.factorId)}>{busy ? "Verifying..." : "Verify and continue"}</Btn>
          </>
        )}
      </div>
    );
  }
  return wrap(
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Enter your authenticator code</div>
      <div style={{ fontSize: 13, color: C.textSecondary, marginBottom: 14 }}>Open your authenticator app and enter the 6-digit code for PracticeOS.</div>
      <Input label="6-digit code" value={code} onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))} />
      <Btn variant="primary" disabled={busy} onClick={challengeExisting}>{busy ? "Verifying..." : "Continue"}</Btn>
    </div>
  );
}
